/**
 * MobileOptimizerAllTrades — FRAME 3 (all-trades filter/sort) + FRAME B (risk-grouped).
 * DD-MOBILE-OPTIMIZER-0724.
 *
 * A sticky type-filter + sort strip over the trade sequences. Sort=Risk re-sections the list
 * under the engine's four risk profiles as left-rule editorial headers (Frame B), in plain
 * English (risk_display_label). trade_type distinguishes 2-team vs 3-team rows; a 3-team row
 * carries the dataPurple left-rule + 3-Team marker and routes to the cycle sheet.
 */
import React, { useMemo, useState } from 'react';
import {
  C, SP, FS, R, PRIMARY, BORDER, BORDER_SOFT, NUM, withAlpha, sectionHeaderStyle, pillStyle, riskColor,
} from './optimizerTokens';

const TYPE_FILTERS = [['all', 'All'], ['two', '2-Team'], ['three', '3-Team'], ['pick', 'W/ Pick']];
const SORTS = [['pts', 'Pts'], ['accept', 'Accept'], ['risk', 'Risk']];
const RISK_ORDER = ['safe_bet', 'high_upside', 'category_punt', 'dynasty_play'];
const RISK_LABEL = { safe_bet: 'Safe Bets', high_upside: 'High Upside', category_punt: 'Category Trade-Offs', dynasty_play: 'Dynasty Plays' };
const RISK_HINT = { safe_bet: 'positive even if it misses', high_upside: 'big ceiling, longer odds', category_punt: 'gives up a strength', dynasty_play: 'long-term over this week' };

function Chip({ active, children, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ fontSize: FS['2xs'], fontWeight: 700, padding: `4px 9px`, borderRadius: R.none, cursor: 'pointer',
        background: active ? withAlpha(PRIMARY, 0.14) : 'none',
        color: active ? PRIMARY : C.neutral[400],
        border: `1px solid ${active ? withAlpha(PRIMARY, 0.32) : withAlpha(C.neutral[700], 0.5)}` }}>
      {children}
    </button>
  );
}

function Row({ sequence, onOpen }) {
  const pkg = sequence.trade_package || {};
  const isThree = pkg.trade_type === 'three_team';
  const send = (pkg.user_sends?.players || [])[0]?.player_name || '—';
  const recv = (pkg.user_receives?.players || [])[0]?.player_name || '—';
  const net = pkg.net_value;
  const accept = pkg.acceptance_probability;
  const sens = sequence.sensitivity || {};
  return (
    <button type="button" onClick={onOpen} data-testid={isThree ? 'all-row-3team' : 'all-row'}
      style={{ width: '100%', textAlign: 'left', padding: `7px ${SP.sm}`, borderBottom: BORDER_SOFT,
        display: 'flex', alignItems: 'center', gap: 9, background: 'none', cursor: 'pointer',
        borderLeft: isThree ? `3px solid ${C.dataPurple}` : 'none' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: FS.xs, fontWeight: 700, color: C.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {send} → {recv}{isThree ? ' (cycle)' : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          {isThree && <span style={pillStyle(C.dataPurple, 0.16)}>3-Team</span>}
          {sens.risk_display_label && <span style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>{sens.risk_display_label}</span>}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: FS['2xs'], color: C.neutral[500], ...NUM }}>
          ▼{fmt(sens.downside_gain)} ▲{fmt(sens.upside_gain)}
        </div>
        <div style={{ fontSize: FS['2xs'], fontWeight: 600, color: accept >= 0.5 ? C.success : C.warning, ...NUM }}>{accept != null ? `${Math.round(accept * 100)}%` : '—'}</div>
      </div>
      <div style={{ width: 30, textAlign: 'right', flexShrink: 0, fontSize: FS.sm, fontWeight: 900, color: net >= 0 ? C.success : C.error, ...NUM }}>
        {net >= 0 ? `+${net}` : net}
      </div>
    </button>
  );
}

const fmt = (v) => (v == null ? '—' : (v >= 0 ? `+${Math.round(v)}` : Math.round(v)));

export default function MobileOptimizerAllTrades({ trades, onBack, onOpen }) {
  const [typeF, setTypeF] = useState('all');
  const [sort, setSort] = useState('pts');
  const all = trades?.trade_engine?.trade_sequences || [];

  const filtered = useMemo(() => {
    let r = all.filter((s) => {
      const p = s.trade_package || {};
      if (typeF === 'two') return p.trade_type !== 'three_team';
      if (typeF === 'three') return p.trade_type === 'three_team';
      if (typeF === 'pick') return (p.user_receives?.picks || []).length > 0 || (p.user_sends?.picks || []).length > 0;
      return true;
    });
    if (sort === 'accept') r = [...r].sort((a, b) => (b.trade_package?.acceptance_probability || 0) - (a.trade_package?.acceptance_probability || 0));
    else if (sort === 'pts') r = [...r].sort((a, b) => (b.trade_package?.net_value || 0) - (a.trade_package?.net_value || 0));
    return r;
  }, [all, typeF, sort]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((s) => { const p = s.sensitivity?.risk_profile || 'other'; (g[p] = g[p] || []).push(s); });
    return g;
  }, [filtered]);

  return (
    <div data-testid="all-trades">
      <button type="button" onClick={onBack} style={{ fontSize: FS.xs, color: PRIMARY, background: 'none', border: 'none', padding: `${SP.xs} 0`, cursor: 'pointer' }}>← Feed</button>

      {/* DD-RO-BUGS5-0725 B7 — the decorative "Gaps · Free Agents · Trades · Standings" sub-tab row was
          removed. They were plain <span>s with no onClick and no destination: this mobile optimizer is a
          single-scroll feed (parent MobileOptimizer), not a tabbed router, and it has no Free Agents or
          Standings surface at all. The row implied four navigable tabs when only this Trades view exists;
          Gaps is reached via "← Feed". §14.0 mobile-native — do not fake desktop chrome. */}

      {/* sticky filter/sort strip */}
      <div style={{ display: 'flex', gap: 5, alignItems: 'center', overflowX: 'auto', scrollbarWidth: 'none',
        padding: `7px 0`, borderBottom: BORDER, position: 'sticky', top: 0, background: C.neutral[900], zIndex: 5 }}>
        {TYPE_FILTERS.map(([v, l]) => <Chip key={v} active={typeF === v} onClick={() => setTypeF(v)}>{l}</Chip>)}
        <span style={{ width: 1, height: 16, background: withAlpha(C.neutral[700], 0.5) }} />
        <span style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>Sort:</span>
        {SORTS.map(([v, l]) => <Chip key={v} active={sort === v} onClick={() => setSort(v)}>{l}</Chip>)}
      </div>

      {/* FRAME B — risk-grouped when sort=Risk; else flat ranked list (frame 3) */}
      {sort === 'risk' ? (
        <div data-testid="risk-grouped">
          {RISK_ORDER.filter((p) => grouped[p]?.length).map((p) => {
            const shs = sectionHeaderStyle(riskColor(p));
            return (
              <div key={p}>
                <div style={shs.row}><span style={shs.bar} /><span style={shs.label}>{RISK_LABEL[p]}</span>
                  <span style={{ marginLeft: 'auto', fontSize: FS['2xs'], color: C.neutral[500] }}>{RISK_HINT[p]} · {grouped[p].length}</span></div>
                {grouped[p].map((s, i) => <Row key={i} sequence={s} onOpen={() => onOpen('trade', s)} />)}
              </div>
            );
          })}
        </div>
      ) : (
        <div data-testid="flat-trades">
          {filtered.map((s, i) => <Row key={i} sequence={s} onOpen={() => onOpen('trade', s)} />)}
          {filtered.length === 0 && <div style={{ padding: SP.md, textAlign: 'center', color: C.neutral[400], fontSize: FS.sm }}>No trades match.</div>}
        </div>
      )}
    </div>
  );
}
